/**
 * ProjectTicket — Project ticket content for EditorPanel.
 * Extracted from EditorPanel.tsx (refactoring, no behavior changes).
 */

import { useState, useRef } from 'react';
import { useProjectStore, useUIStore } from '../../../stores';
import { useAuthStore } from '../../../stores/authStore';
import { useIsReadOnlyViewer } from '../../../hooks/useShareViewer';
import { Button } from '../../common';
import LoginModal from '../../common/LoginModal';
import CreateProjectModal from '../../common/CreateProjectModal';
import { exportProjectZip, downloadBlob as downloadBlobFile } from '../../../services/projectExport';
import { syncProjectToServer } from '../../../services/projectSync';
import { serverLogout } from '../../../services/apiFetch';

export default function ProjectTicket() {
  const meta = useProjectStore((s) => s.meta);
  const exportProject = useProjectStore((s) => s.exportProject);

  const addNotification = useUIStore((s) => s.addNotification);
  const showConfirm = useUIStore((s) => s.showConfirm);
  const setProgressOverlay = useUIStore((s) => s.setProgressOverlay);

  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const isReadOnly = useIsReadOnlyViewer();

  // Local state
  const [isSyncing, setIsSyncing] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);

  // File input ref for JSON/ZIP open
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ============================================
  // FILE OPERATIONS
  // ============================================
  const handleOpen = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.name.endsWith('.zip')) {
      try {
        addNotification({ type: 'info', title: 'Importing ZIP...' });
        const { importProjectZip: importZip } = await import('../../../services/projectImport');
        const { mergeProjectData } = await import('../../../services/projectMerge');
        const store = useProjectStore.getState();
        const siteId = store.meta.siteId;
        // Don't pass siteId — skip R2 uploads for fast import (blob URLs).
        // Assets will be uploaded to R2 when user clicks "Save to Server" via syncProjectToServer.
        const result = await importZip(file, undefined, (msg) => console.log('[Import]', msg));

        // Check if current project has data worth preserving
        const currentData = store.data;
        const hasExistingData = !!siteId
          || currentData.indications.length > 0
          || currentData.overlays.length > 0
          || currentData.orthophotos.length > 0
          || currentData.flightPaths.length > 0
          || currentData.geoPhotos.length > 0
          || currentData.videoStrips.length > 0
          || (currentData.sldDiagrams && currentData.sldDiagrams.length > 0);

        if (hasExistingData) {
          // Show Replace/Merge dialog
          // Use fresh store ref in callbacks so siteId/meta is up-to-date
          showConfirm({
            title: 'Import ZIP Backup',
            message: 'This project already has data. How would you like to import the backup?\n\n\u2022 Replace All \u2014 clear current data, load backup\n\u2022 Merge \u2014 restore backup data and keep new additions',
            confirmLabel: 'Merge',
            cancelLabel: 'Replace All',
            variant: 'default',
            onConfirm: () => {
              const s = useProjectStore.getState();
              // Merge: union by ID, incoming wins on conflict
              const merged = mergeProjectData(s.data, result.data as any);
              // Keep current meta (siteId, siteName) — don't overwrite with ZIP meta
              s.loadProject(merged as any, { siteId: s.meta.siteId, siteName: s.meta.siteName });
              addNotification({ type: 'success', title: 'ZIP merged', message: 'Backup restored, new additions kept' });
            },
            onCancel: () => {
              const s = useProjectStore.getState();
              // Replace all: keep current siteId so auto-save still works
              const importMeta = s.meta.siteId
                ? { siteId: s.meta.siteId, siteName: s.meta.siteName || result.meta.siteName }
                : result.meta;
              s.loadProject(result.data as any, importMeta);
              addNotification({ type: 'success', title: 'Project replaced from ZIP' });
            },
          });
        } else {
          // No existing data — direct load
          store.loadProject(result.data as any, result.meta);
          addNotification({ type: 'success', title: 'Project imported from ZIP' });
        }
      } catch (err) {
        addNotification({ type: 'error', title: 'ZIP import failed', message: err instanceof Error ? err.message : 'Invalid ZIP file' });
      }
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const projectData = JSON.parse(ev.target?.result as string);
          useProjectStore.getState().loadProject(projectData, { siteName: file.name });
          addNotification({ type: 'success', title: 'Project loaded', message: file.name });
        } catch {
          addNotification({ type: 'error', title: 'Failed to load project', message: 'Invalid JSON file' });
        }
      };
      reader.readAsText(file);
    }
    e.target.value = '';
  };

  const handleExportZip = async () => {
    addNotification({ type: 'info', title: 'Exporting ZIP...', message: 'Downloading assets and packaging' });
    try {
      const projectData = exportProject();
      const zipBlob = await exportProjectZip(projectData, meta, (msg) => {
        console.log('[Export]', msg);
      });
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const filename = `SoleadLab_${meta.siteName || 'Project'}_${timestamp}.zip`;
      downloadBlobFile(zipBlob, filename);
      addNotification({ type: 'success', title: 'ZIP exported', message: filename });
    } catch (err) {
      console.error('ZIP export error:', err);
      addNotification({ type: 'error', title: 'Export failed', message: err instanceof Error ? err.message : 'Unknown error' });
    }
  };

  // ============================================
  // AUTH ACTIONS
  // ============================================
  const handleLogout = async () => {
    await serverLogout();
    addNotification({ type: 'info', title: 'Logged out' });
  };

  const handleAdminToggle = () => {
    setShowLoginModal(true);
  };

  // ============================================
  // SERVER SYNC
  // ============================================
  const handleSaveToServer = async () => {
    const siteId = useProjectStore.getState().meta.siteId;
    if (!isAuthenticated) {
      addNotification({ type: 'warning', title: 'Login required', message: 'Please log in to save to server' });
      return;
    }
    if (!siteId) {
      setShowCreateProject(true);
      return;
    }
    setIsSyncing(true);
    useProjectStore.getState().setSaveStatus('saving');
    setProgressOverlay({ visible: true, label: 'Preparing upload...', current: 0, total: 1 });
    try {
      const currentData = useProjectStore.getState().exportProject();
      console.log('[SaveToServer] siteId:', siteId);
      console.log('[SaveToServer] Data size:', JSON.stringify(currentData).length, 'bytes');
      console.log('[SaveToServer] Overlays:', currentData.overlays.length, 'Orthos:', currentData.orthophotos.length, 'Indications:', currentData.indications.length);
      const updatedData = await syncProjectToServer(siteId, currentData, (cur, tot, msg) => {
        setProgressOverlay({ visible: true, label: msg, current: cur, total: tot });
      });
      // Update store with R2 URLs so future saves don't re-upload
      useProjectStore.getState().setData(updatedData);

      // syncProjectToServer sets saveStatus='conflict' on a 409 from the
      // base PUT (and returns instead of throwing). Don't paper over it
      // with the success path — the conflict modal in EditorPage drives
      // the resolution UI.
      if (useProjectStore.getState().meta.saveStatus === 'conflict') {
        console.warn('[SaveToServer] save reported version conflict — see Save Conflict dialog');
        return;
      }

      useProjectStore.getState().markSaved();
      useProjectStore.getState().setSaveStatus('saved');
      addNotification({ type: 'success', title: 'Saved to server' });
      console.log('[SaveToServer] Save complete');
      setTimeout(() => {
        const s = useProjectStore.getState().meta.saveStatus;
        if (s === 'saved') useProjectStore.getState().setSaveStatus('idle');
      }, 3000);
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Unknown error';
      console.error('[SaveToServer] FAILED:', err);
      useProjectStore.getState().setSaveStatus('error');
      addNotification({ type: 'error', title: 'Save failed', message: errMsg });
      // Failsafe alert so the user always sees critical save errors
      window.alert(`Save to server failed!\n\n${errMsg}`);
    } finally {
      setIsSyncing(false);
      setProgressOverlay(null);
    }
  };

  const handleProjectCreated = (siteId: string, siteName: string) => {
    useProjectStore.getState().setMeta({ siteId, siteName });
    setShowCreateProject(false);
    addNotification({ type: 'success', title: `Created "${siteName}"` });
    // Re-trigger save now that siteId exists
    setTimeout(() => handleSaveToServer(), 0);
  };

  return (
    <>
      {/* Hidden file input for JSON/ZIP */}
      <input ref={fileInputRef} type="file" accept=".json,.slp,.zip" onChange={handleFileSelect} style={{ display: 'none' }} />

      <div className="ep-ticket-content" data-testid="project-ticket">
        {isReadOnly ? (
          /* F-02E-2B: share viewers see no file/account controls.
             Save/Open/Import/Export/Login/Logout are all owner surfaces;
             Export ZIP is hidden here too because, even though it's
             client-side, handing a visitor a downloadable copy of the
             full project data wasn't part of the share-link contract. */
          <div
            className="ep-section-label"
            data-testid="project-ticket-readonly-notice"
          >
            Read-only shared view. File and account controls are not
            available.
          </div>
        ) : (
          <>
            <div className="ep-section-label">File</div>
            <Button variant="secondary" size="sm" fullWidth onClick={handleOpen}>
              Open JSON / ZIP
            </Button>
            <Button variant="secondary" size="sm" fullWidth onClick={() => {
              if (!isAuthenticated) {
                addNotification({ type: 'warning', title: 'Login required', message: 'Please log in to access server projects' });
                return;
              }
              useUIStore.getState().setShowProjectSelect(true);
            }}>
              Open from Server
            </Button>
            <Button variant="secondary" size="sm" fullWidth onClick={handleExportZip}>
              Export Package (ZIP)
            </Button>
            <Button variant="primary" size="sm" fullWidth onClick={handleSaveToServer} disabled={isSyncing}>
              {isSyncing ? 'Saving...' : 'Save to Server'}
            </Button>
            <div className="ep-divider" />
            <div className="ep-section-label">Account</div>
            <Button variant={isAuthenticated ? 'secondary' : 'warning'} size="sm" fullWidth onClick={handleAdminToggle}>
              {isAuthenticated ? 'Account' : 'Login'}
            </Button>
            <Button variant="danger" size="sm" fullWidth onClick={handleLogout}>
              Logout
            </Button>
          </>
        )}
      </div>

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={() => {
          addNotification({ type: 'success', title: 'Logged in successfully' });
          useUIStore.getState().setShowProjectSelect(true);
        }}
      />

      {/* Create Project Modal (shown when saving without siteId) */}
      <CreateProjectModal
        isOpen={showCreateProject}
        onClose={() => setShowCreateProject(false)}
        onCreated={handleProjectCreated}
        defaultName={meta.siteName || ''}
      />
    </>
  );
}
